import {
  CCY_SCALE,
  CCY_SYMBOLS,
  D,
  MONEY_SCALE,
  fmtAmt,
  type CcyCode,
  type Money,
  type OrderDetail,
} from '@wave/shared';
import { naturalRate } from './rate';

/** Число с ru-RU разрядами и точностью валюты (как в fmtAmt, но без символа). */
function fmtNum(amt: Money, ccy: CcyCode): string {
  const maxDigits = Math.min(CCY_SCALE[ccy] ?? 2, MONEY_SCALE);
  return D(amt).toNumber().toLocaleString('ru-RU', { maximumFractionDigits: maxDigits });
}

/**
 * Сумма для сообщения клиенту. Фиат — с привычным символом (₽, €, ¥).
 * USDT (и любая валюта с символом «$», кроме USD) — с ISO-кодом: в тексте,
 * который клиент читает вне приложения, «$» путается с долларом.
 */
function fmtClientAmt(amt: Money, ccy: CcyCode): string {
  if (CCY_SYMBOLS[ccy] === '$' && ccy !== 'USD') return `${fmtNum(amt, ccy)} ${ccy}`;
  return fmtAmt(amt, ccy);
}

/**
 * Готовый текст для менеджера — что отправить клиенту в чат:
 * сколько принимаем, по какому курсу, сколько клиент получает и номер заявки.
 *
 * Курс считается из фактических сумм (эффективный клиентский курс: сколько
 * платит ÷ сколько получает), направление — «естественное» (котировка ≥ 1),
 * поэтому строка читается как «1 USDT = 83,98 ₽» независимо от направления
 * сделки. При наличии коэффициента clGiveAmt/clGetAmt ≠ clRate — здесь
 * осознанно показываем именно эффективный курс, который видит клиент.
 */
export function buildClientMessage(order: OrderDetail): string {
  const give = fmtClientAmt(order.clGiveAmt, order.clGiveCcy);
  const get = fmtClientAmt(order.clGetAmt, order.clGetCcy);
  const lines: string[] = [`Заявка ${order.id}`, '', `Принимаем: ${give}`];

  const rate = naturalRate(order.clGiveAmt, order.clGiveCcy, order.clGetAmt, order.clGetCcy);
  if (rate) {
    const [numCcy, denCcy] = rate.pair.split('/') as [CcyCode, CcyCode];
    lines.push(`Курс: 1 ${denCcy} = ${fmtClientAmt(rate.value, numCcy)}`);
    // Курс нормализован (≥1), поэтому операция зависит от направления сделки:
    // если валюта «принимаем» — числитель курса (отдаём за 1 получаем) → делим,
    // иначе (курс = получаем за 1 отдаём) → умножаем. Число курса форматируем
    // так же, как в строке «Курс», чтобы арифметика с ней совпадала.
    const op = numCcy === order.clGiveCcy ? '÷' : '×';
    lines.push(`К получению: ${give} ${op} ${fmtNum(rate.value, numCcy)} = ${get}`);
  } else {
    lines.push(`К получению: ${get}`);
  }

  return lines.join('\n');
}
